import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { sortedStates, getDistrictsForState } from '../../data/indianLocations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const BusinessSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [availableDistricts, setAvailableDistricts] = useState([]);
  
  const [formData, setFormData] = useState({
    business_name: '',
    contact_person: '',
    mobile_number: '',
    email: '',
    address: '',
    postal_code: '',
    city: '',
    district: '',
    state: '',
    state_code: '',
    gst_number: '',
    business_type: 'both',
    inventory_model: 'grn_based',
    industry: 'retail',
    bank_name: '',
    bank_account_name: '',
    bank_account_number: '',
    bank_ifsc_code: '',
    bank_branch_name: ''
  });

  const industries = [
    { value: 'retail', label: 'Retail Shop' },
    { value: 'restaurant', label: 'Restaurant/Cafe' },
    { value: 'pharmacy', label: 'Medical Store/Pharmacy' },
    { value: 'electronics', label: 'Electronics Store' },
    { value: 'grocery', label: 'Grocery Store' },
    { value: 'fashion', label: 'Fashion/Clothing' },
    { value: 'services', label: 'Services' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  // Update districts when state changes
  useEffect(() => {
    if (formData.state_code) {
      const districts = getDistrictsForState(formData.state_code);
      setAvailableDistricts(districts);
    } else {
      setAvailableDistricts([]);
    }
  }, [formData.state_code]);

  // Auto-set inventory model based on business type
  useEffect(() => {
    if (formData.business_type === 'service_only') {
      setFormData(prev => ({ ...prev, inventory_model: 'no_inventory' }));
    }
  }, [formData.business_type]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/settings/business`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(response.data);
      if (response.data.logo) {
        setLogoPreview(response.data.logo);
      }
    } catch (err) {
      setError('Failed to load business settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for state selection
    if (name === 'state_code') {
      const selectedState = sortedStates.find(s => s.code === value);
      setFormData(prev => ({
        ...prev,
        state_code: value,
        state: selectedState ? selectedState.name : '',
        district: '' // Reset district when state changes
      }));
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 50 * 1024) {
      setError('Logo must be less than 50KB');
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.post(`${API}/settings/business/logo`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setLogoPreview(response.data.logo);
      setSuccess('Logo uploaded successfully');
    } catch (err) {
      setError('Failed to upload logo');
    }
  };

  const handleRemoveLogo = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      await axios.delete(`${API}/settings/business/logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogoPreview(null);
      setSuccess('Logo removed successfully');
    } catch (err) {
      setError('Failed to remove logo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('pos_token');
      await axios.put(`${API}/settings/business`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Business settings saved successfully!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="business-settings-page" className="max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/gposp/settings')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="back-button"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Business Settings</h1>
          <p className="text-slate-500">Manage your business profile and configuration</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700" data-testid="error-message">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700" data-testid="success-message">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Logo Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Business Logo</h2>
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 bg-slate-100 rounded-xl flex items-center justify-center overflow-hidden">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
              ) : (
                <span className="text-slate-400 text-3xl font-bold">
                  {formData.business_name?.charAt(0) || 'B'}
                </span>
              )}
            </div>
            <div className="space-y-2">
              <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="logo-upload"
                />
                Upload Logo
              </label>
              {logoPreview && (
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="block text-red-600 text-sm hover:underline"
                  data-testid="remove-logo"
                >
                  Remove Logo
                </button>
              )}
              <p className="text-xs text-slate-500">Max size: 50KB</p>
            </div>
          </div>
        </div>

        {/* Business Profile */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Business Profile</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Business Name</label>
              <input
                type="text"
                name="business_name"
                value={formData.business_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="business-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Person</label>
              <input
                type="text"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="contact-person-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mobile Number</label>
              <input
                type="text"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="mobile-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="email-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={2}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="address-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">State / UT *</label>
              <select
                name="state_code"
                value={formData.state_code || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="state-select"
              >
                <option value="">Select State / UT</option>
                {sortedStates.map(state => (
                  <option key={state.code} value={state.code}>
                    {state.name} {state.type === 'UT' ? '(UT)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">District *</label>
              <select
                name="district"
                value={formData.district || ''}
                onChange={handleChange}
                disabled={!formData.state_code}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent disabled:bg-slate-100"
                data-testid="district-select"
              >
                <option value="">Select District</option>
                {availableDistricts.map(district => (
                  <option key={district} value={district}>{district}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">City / Town</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Enter city or town name"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="city-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Postal Code</label>
              <input
                type="text"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                maxLength="6"
                placeholder="6-digit PIN code"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="postal-code-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">GST Number</label>
              <input
                type="text"
                name="gst_number"
                value={formData.gst_number || ''}
                onChange={handleChange}
                placeholder="e.g., 07AAACT2727Q1ZV"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="gst-input"
              />
            </div>
          </div>
        </div>

        {/* Business Configuration */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Business Configuration</h2>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-3">Industry</label>
            <select
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              className="w-full md:w-1/2 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              data-testid="industry-select"
            >
              {industries.map(ind => (
                <option key={ind.value} value={ind.value}>{ind.label}</option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-3">Business Type</label>
            <div className="flex flex-wrap gap-4">
              {[
                { value: 'product_only', label: 'Product Only' },
                { value: 'service_only', label: 'Service Only' },
                { value: 'both', label: 'Both (Product & Service)' }
              ].map(type => (
                <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="business_type"
                    value={type.value}
                    checked={formData.business_type === type.value}
                    onChange={handleChange}
                    className="w-4 h-4 text-violet-600"
                    data-testid={`business-type-${type.value}`}
                  />
                  <span className="text-slate-700">{type.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">Inventory Model</label>
            {formData.business_type === 'service_only' && (
              <p className="text-sm text-amber-600 mb-2">
                * Service-only businesses automatically use "No Inventory" model
              </p>
            )}
            <div className="flex flex-wrap gap-4">
              {[
                { value: 'no_inventory', label: 'No Inventory', desc: 'For services, no stock tracking' },
                { value: 'stock_only', label: 'Stock Only', desc: 'Direct stock entry without purchase orders' },
                { value: 'grn_based', label: 'GRN Based (Default)', desc: 'Full purchase & stock management' }
              ].map(model => (
                <label 
                  key={model.value} 
                  className={`flex items-start gap-2 cursor-pointer p-3 rounded-lg border ${
                    formData.inventory_model === model.value 
                      ? 'border-violet-500 bg-violet-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  } ${formData.business_type === 'service_only' && model.value !== 'no_inventory' ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="radio"
                    name="inventory_model"
                    value={model.value}
                    checked={formData.inventory_model === model.value}
                    onChange={handleChange}
                    disabled={formData.business_type === 'service_only' && model.value !== 'no_inventory'}
                    className="w-4 h-4 text-violet-600 mt-0.5"
                    data-testid={`inventory-model-${model.value}`}
                  />
                  <div>
                    <span className="text-slate-700 font-medium">{model.label}</span>
                    <p className="text-xs text-slate-500">{model.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Bank Details Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Bank Details</h2>
          <p className="text-sm text-slate-500 mb-4">Bank details will appear on your A4 GST invoices</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Account Holder Name</label>
              <input
                type="text"
                name="bank_account_name"
                value={formData.bank_account_name || ''}
                onChange={handleChange}
                placeholder="e.g., M/s Your Business Name"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                data-testid="bank-account-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bank Name</label>
              <input
                type="text"
                name="bank_name"
                value={formData.bank_name || ''}
                onChange={handleChange}
                placeholder="e.g., State Bank of India"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                data-testid="bank-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Account Number</label>
              <input
                type="text"
                name="bank_account_number"
                value={formData.bank_account_number || ''}
                onChange={handleChange}
                placeholder="e.g., 1234567890"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                data-testid="bank-account-number-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">IFSC Code</label>
              <input
                type="text"
                name="bank_ifsc_code"
                value={formData.bank_ifsc_code || ''}
                onChange={handleChange}
                placeholder="e.g., SBIN0001234"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 uppercase"
                data-testid="bank-ifsc-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Branch Name</label>
              <input
                type="text"
                name="bank_branch_name"
                value={formData.bank_branch_name || ''}
                onChange={handleChange}
                placeholder="e.g., Main Branch, Mumbai"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                data-testid="bank-branch-input"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-between">
          <button
            type="button"
            onClick={() => navigate('/gposp/settings/reset-business')}
            className="px-6 py-3 bg-red-50 text-red-600 font-medium rounded-xl hover:bg-red-100 transition-all flex items-center gap-2"
            data-testid="reset-business-btn"
          >
            Reset Business Data
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-8 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold rounded-xl hover:from-violet-700 hover:to-purple-700 transition-all disabled:opacity-50"
            data-testid="save-button"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BusinessSettings;
